import { useCallback, useMemo } from 'react';
import produce from 'immer';
import { useQuery, useMutation, useInfiniteQuery, InfiniteData } from 'react-query';
import message from 'antd/es/message';

import {
  formatNetworth,
  request,
  useCurrentUserId,
  L2TxReqParams,
  queryClient,
  errorToString,
  toastError,
} from '@/utils';

import { ArticleDepositor, ArticleEarner } from '../types';
import { invalidArticleDetail } from './detail';

const transDepositor = (x: ArticleDepositor) => {
  x._rewardUsdValue = formatNetworth(x.deposit_usd_value);
};

export const useArticleTopDepositors = (id: number) => {
  const url = '/article/top_depositors';
  const currentAddress = useCurrentUserId();

  const select = useCallback((res: { depositors: ArticleDepositor[]; total_count: number }) => {
    const depositors = produce(res.depositors, draft => {
      draft
        .sort((m, n) => (n.deposit_usd_value || 0) - (m.deposit_usd_value || 0))
        .forEach(transDepositor);
    });

    return {
      depositors,
      total_count: res.total_count,
    };
  }, []);

  return useQuery(
    [url, currentAddress, id],
    () => {
      return request.get<{ depositors: ArticleDepositor[]; total_count: number }>(url, {
        id,
        limit: 5,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!id,
      select,
    },
  );
};

export const useArticleDepositors = (id: number) => {
  const currentAddress = useCurrentUserId();
  const url = '/article/depositors';

  const select = useCallback(
    (data: InfiniteData<{ depositors: ArticleDepositor[]; total_count: number }>) => {
      return produce(data, draft => {
        draft.pages.forEach(data => {
          data.depositors.forEach(transDepositor);
        });
      });
    },
    [],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    [url, currentAddress, id],
    async ({ pageParam = 0 }) => {
      const params = {
        id,
        start: pageParam,
        limit: 100,
      };

      return request.get<{ depositors: ArticleDepositor[]; total_count: number }>(url, params);
    },
    {
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const total = pages.reduce((s, n) => s + n.depositors.length, 0);

        if (total >= lastPage?.total_count) {
          return;
        }
        return total;
      },
      select,
      onError(err: any) {
        message.error(errorToString(err));
      },
    },
  );

  const result = useMemo(
    () => ({
      depositors: data?.pages.flatMap(x => x.depositors),
      total_count: data?.pages?.[0].total_count || 0,
    }),
    [data],
  );

  return {
    result,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
  };
};

export const changeArticleDepositorsFollowing = (
  articleId: number,
  userId: string,
  currentAddress?: string,
) => {
  queryClient.setQueriesData<InfiniteData<{ depositors: ArticleDepositor[] }>>(
    ['/article/depositors', currentAddress, articleId],
    pre => {
      if (!pre) {
        return pre!;
      }

      return produce(pre, pre => {
        if (pre) {
          pre.pages?.some(x => {
            return x.depositors.some(x => {
              if (x.id === userId) {
                x.is_following = !x.is_following;

                return true;
              }

              return false;
            });
          });
        }
      });
    },
  );
};

const invalidTopDepositors = (currentAddress?: string, id?: number) => {
  queryClient.invalidateQueries(['/article/top_depositors', currentAddress, id]);
};

export const useArticleUserDepositor = (id: number, userId?: string) => {
  const url = '/article/depositor';

  const select = useCallback((res: { depositor: ArticleDepositor }) => {
    return produce(res.depositor, draft => {
      transDepositor(draft);
    });
  }, []);

  return useQuery(
    [url, userId, id],
    () => {
      return request.get<{ depositor: ArticleDepositor }>(url, {
        id,
        user_id: userId!,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!id && !!userId,
      select,
    },
  );
};

const invalidDepositor = (currentAddress?: string, id?: number) => {
  queryClient.invalidateQueries(['/article/depositor', currentAddress, id]);
};

export const useArticleTip = (id: number) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    (tx: L2TxReqParams) => {
      return request.post<{ is_success: boolean }>('/article/deposit', { id, tx });
    },
    {
      onSuccess({ is_success }) {
        if (is_success) {
          invalidTopDepositors(currentAddress, id);
          invalidDepositor(currentAddress, id);
          invalidArticleDetail(id, currentAddress);
          message.success('Deposited');
        } else {
          message.error('failed to tip, please try later');
        }
      },
      onError(err: any) {
        toastError(err, 'failed to tip, please try later');
      },
    },
  );
};

const transEaner = (x: ArticleEarner) => {
  x._earnUsdValue = formatNetworth(x.earn_usd_value);
};

export const useArticleTopEaners = (id: number) => {
  const url = '/article/top_earners';
  const currentAddress = useCurrentUserId();

  const select = useCallback((res: { earners: ArticleEarner[] }) => {
    const earners = produce(res.earners, draft => {
      draft.sort((m, n) => (n.earn_usd_value || 0) - (m.earn_usd_value || 0)).forEach(transEaner);
    });

    return {
      earners,
    };
  }, []);

  return useQuery(
    [url, currentAddress, id],
    () => {
      return request.get<{ earners: ArticleEarner[] }>(url, {
        id,
        limit: 5,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!id,
      select,
    },
  );
};

export const useArticleUserEaner = (id: number, userId?: string) => {
  const url = '/article/earner';

  const select = useCallback((res: { earner: ArticleEarner }) => {
    return produce(res.earner, draft => {
      transEaner(draft);
    });
  }, []);

  return useQuery(
    [url, userId, id],
    () => {
      return request.get<{ earner: ArticleEarner }>(url, {
        id,
        user_id: userId!,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!id && !!userId,
      select,
    },
  );
};
